import React, { useState, useEffect } from 'react';
import { EmailAddressHelper } from '../../utils/helpers';
import LanguageStringResolver from '../fields/language/LanguageStringResolver';
import ApplicationMessages from '../inAppMessages/ApplicationMessages';
import HighlightOnInAppMessage from '../inAppMessages/HighlightOninAppMessage';
import Logger, { LOG_LEVELS, ALL_MESSAGES } from '../../utils/logger';
import MultiCompanySelect from '../selectors/company/MultiCompanySelect';
import { Link } from 'react-router-dom';
import { UserRoleEnum, UserFormModeEnum } from '../../utils/enums';
import getClientConfig from '../../utils/getClientConfig';

const FORM_NAME = 'USER_FORM_V2';
const log = new Logger(FORM_NAME, LOG_LEVELS.DEBUG);
const toUsersListUrl = '/UserManagement';
const ALL_COMPANIES_GRANT = '*';

const UserForm = (props) => {
  const [user, setUser] = useState(props.user);
  const [allCompaniesChecked, setAllCompaniesChecked] = useState(props.selectedCompanies?.includes(ALL_COMPANIES_GRANT));
  const [selectedCompanies, setSelectedCompanies] = useState(props.selectedCompanies); // This values should be default value passed from View User Page
  useEffect(() => {
    return () => {
      log.clearAllDictionaryErrors();
    }
  }, []);

  const onFieldChange = (e) => {
    const updatedUser = Object.assign({}, user);
    updatedUser[e.target.name] = e.target.value;
    setUser(updatedUser);
  }

  const onAllCompaniesChange = (e) => {
    setAllCompaniesChecked(e.target.checked);
  }

  const onCompanySelectionHandler = (e) => {
    let companies = selectedCompanies.slice();
    companies.push(e.value);
    setSelectedCompanies(companies);
  }

  const onCompanyDeselectionHandler = (e) => {
    let companies = selectedCompanies.filter(t => t !== e.value);
    setSelectedCompanies(companies);
  }

  const onRoleChange = (e) => {
    const updatedUser = Object.assign({}, user);
    updatedUser.role = e.target.value;
    setUser(updatedUser);
  }

  const onSaveChanges = () => {
    if (validateFields()) {
      let companyGrants;
      if (allCompaniesChecked) {
        companyGrants = [ALL_COMPANIES_GRANT];
      } else {
        companyGrants = selectedCompanies.filter(c => c !== ALL_COMPANIES_GRANT);
      }
      props.editHandler(user, companyGrants);
    }
  }

  const validateFields = () => {
    log.clearAllDictionaryErrors();

    let isValid = true;

    if (user.firstName === '') {
      log.setDictionaryMessage(ALL_MESSAGES.INVALID_FIRST_NAME);
      isValid = false;
    }
    if (user.lastName === '') {
      log.setDictionaryMessage(ALL_MESSAGES.INVALID_LAST_NAME);
      isValid = false;
    }
    if (!EmailAddressHelper.validEmailAddress(user.id)) {
      log.setDictionaryMessage(ALL_MESSAGES.INVALID_EMAIL);
      isValid = false;
    }
    if (!isValid) {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }

    return isValid;
  }

  const onSubmitForm = () => {
    if (validateFields()) {
      props.submitHandler(user);
    }
  }

  const createFormElement = () => {

    const enableReportingUserRole = getClientConfig().featureFlags?.EnableReportingUserRole;
    
    return (
      <div data-testid="user-form">
        <ApplicationMessages
          scopes={[FORM_NAME]}
          errorIds={
            [
              ALL_MESSAGES.INVALID_EMAIL,
              ALL_MESSAGES.INVALID_FIRST_NAME,
              ALL_MESSAGES.INVALID_LAST_NAME,
              ALL_MESSAGES.USER_CREATE_ERROR,
            ]}
          messageType="applicationError"
        />
        <ApplicationMessages
          scopes={[FORM_NAME]}
          errorIds={[ALL_MESSAGES.DUPLICATE_USER_ID]}
          messageType="applicationWarning"
        />
        <form>
          <fieldset>
            <h3 className="mt0"><LanguageStringResolver string='NewUserDetails' /></h3>
            {/* Start of first name field */}
            <label className="required mt10"><LanguageStringResolver string='UserFirstNameHeading' /></label>
            <HighlightOnInAppMessage scopes={[FORM_NAME]} messageIds={[ALL_MESSAGES.INVALID_FIRST_NAME]}>
              <input
                type="text"
                name="firstName"
                value={user.firstName}
                data-testid="input-first-name"
                onChange={onFieldChange}
              />
            </HighlightOnInAppMessage>

            {/* Start of second name field */}
            <label className="required mt10"><LanguageStringResolver string='UserLastNameHeading' /></label>
            <HighlightOnInAppMessage scopes={[FORM_NAME]} messageIds={[ALL_MESSAGES.INVALID_LAST_NAME]}>
              <input
                type="text"
                name="lastName"
                value={user.lastName}
                data-testid="input-last-name"
                onChange={onFieldChange}
              />
            </HighlightOnInAppMessage>

            {/* Start of email address field */}
            <label className="required mt10"><LanguageStringResolver string='UserEmailAddressHeading' /></label>
            <div>
              <HighlightOnInAppMessage scopes={[FORM_NAME]} messageIds={[ALL_MESSAGES.INVALID_EMAIL]}>
                <input
                  type="email"
                  name="id"
                  value={user.id}
                  data-testid="input-email"
                  onChange={onFieldChange}
                  disabled={props.mode === UserFormModeEnum.EDIT}
                />
              </HighlightOnInAppMessage>
            </div>

            {/* Start of Company field */}
            {
              (props.mode === UserFormModeEnum.EDIT) ?
                <div data-testid="company-selector">
                  <h3 className="mt30"><LanguageStringResolver string='CompanySelector' /></h3>
                  <input type="checkbox"
                    id="allCompanies"
                    data-testid="all-companies-checkbox"
                    className="absolute"
                    checked={allCompaniesChecked}
                    onChange={onAllCompaniesChange} />
                  <label htmlFor="allCompanies"><LanguageStringResolver string='UserManagementAllCompaniesText' /></label>
                  <label className="mt10"><LanguageStringResolver string='SelectCompany' /></label>
                  <MultiCompanySelect
                    fieldName={'companyCode'}
                    valueCompanyCode={false}
                    showSelectionCount={false}
                    disabled={allCompaniesChecked}
                    selectionHandler={onCompanySelectionHandler}
                    deselectionHandler={onCompanyDeselectionHandler}
                    selectedCompanyIds={selectedCompanies}
                    companyList={props.allCompanies}
                  />
                </div> :
                null
            }

            <div data-testid="access-radio-buttons">
              <h3 className="mt30 mb0"><LanguageStringResolver string='AccessControl' /></h3>
              <label className="mt10 mb30"><LanguageStringResolver string='AccessControlInfo' /></label>
              <div className="display-block">
                <input type="radio"
                  id="preparer"
                  name="access"
                  data-testid="preparer-radio-button"
                  value={UserRoleEnum.PREPARER.value}
                  defaultChecked={(props.mode === UserFormModeEnum.CREATE) || user?.role === UserRoleEnum.PREPARER.value}
                  disabled={props.mode === UserFormModeEnum.EDIT && props.loggedInUserId === user.id}
                  onChange={onRoleChange} />
                <label className="mt10" htmlFor="preparer"><LanguageStringResolver className=" bold" string='Preparer' />
                  <span>
                    <label className="mt5">
                      <LanguageStringResolver className="mt10" string='PreparerInfo' />
                    </label>
                  </span>
                </label>
              </div>
              { enableReportingUserRole ? <div className="display-block">
                <input type="radio"
                  id="reportingUser"
                  name="access"
                  data-testid="reporting-user-radio-button"
                  value={UserRoleEnum.REPORTING_USER.value}
                  defaultChecked={(props.mode === UserFormModeEnum.EDIT) && user?.role === UserRoleEnum.REPORTING_USER.value}
                  disabled={props.mode === UserFormModeEnum.EDIT && props.loggedInUserId === user.id}
                  onChange={onRoleChange} />
                <label className="mt10" htmlFor="reportingUser"><LanguageStringResolver className=" bold" string='ReportingUser' />
                  <span>
                    <label className="mt5">
                      <LanguageStringResolver className="mt10" string='ReportingUserInfo' />
                    </label>
                  </span>
                </label>
              </div> : null }
              <div className="display-block">
                <input type="radio"
                  id="admin"
                  name="access"
                  data-testid="admin-radio-button"
                  value={UserRoleEnum.ADMIN.value}
                  defaultChecked={(props.mode === UserFormModeEnum.EDIT && user?.role === UserRoleEnum.ADMIN.value)}
                  disabled={props.mode === UserFormModeEnum.EDIT && props.loggedInUserId === user.id}
                  onChange={onRoleChange} />
                <label className="mt10" htmlFor="admin"><LanguageStringResolver className=" bold" string='Administrator' />
                  <span>
                    <label className="mt5">
                      <LanguageStringResolver className="mt10" string='AdministratorInfo' />
                    </label>
                  </span>
                </label>
              </div>
            </div>
            <hr className="mt40 mb20" />

            {
              (props.mode === UserFormModeEnum.CREATE) ? <div>
                <Link
                  className="button secondary float-left"
                  to={toUsersListUrl}
                  data-testid="cancel-create-user"
                >
                  <LanguageStringResolver string="CancelInvite" />
                </Link>
                <Link
                  className="button primary float-right"
                  to="#"
                  data-testid="invite-user"
                  onClick={onSubmitForm}
                >
                  <LanguageStringResolver string="CreateAndSendInvite" />
                </Link>
              </div> :
                <div>
                  <Link
                    className="button secondary float-left"
                    to={toUsersListUrl}
                    data-testid="cancel-edit-user"
                  >
                    <LanguageStringResolver string="CancelInvite" />
                  </Link>
                  <Link
                    className="button primary float-right"
                    to="#"
                    data-testid="save-user-onedit"
                    onClick={onSaveChanges}
                  >
                    <LanguageStringResolver string="SaveUser" />
                  </Link>
                </div>
            }

          </fieldset>
        </form>
      </div>
    );
  }

  return createFormElement();
}

export default UserForm;
